/**
 * RunningReportBand — Himaad's Section 1b, `Run / Running inline` (589:10965).
 *
 * The in-flight state of a report run, as a band between the query card and the
 * grid card: spinner, the report's name, what happens if it takes a while, a
 * live elapsed clock, and Cancel.
 *
 * ## Why this is its own component and not four lines inside ReportView
 *
 * **It owns the ticker.** The clock advances once a second, and this repo has
 * already paid for a ticking value that lived too high in the tree: a `now` in
 * a report page's render reaches the grid's `columns` useMemo, which then
 * rebuilds every second, and the two bugs that produced looked nothing like a
 * clock problem. The interval lives here, so the only thing React re-renders
 * each second is this band. ReportView passes a fixed `startedAt` and never
 * re-renders on its account.
 *
 * That is also why the elapsed clock is not hoisted into `ReportBand` itself —
 * `ReportBand` is rendered by five other callers that have no clock and must
 * not acquire a timer.
 *
 * ## Why the hand-off sentence is conditional
 *
 * "If it passes twenty seconds it carries on in the tray and you get your table
 * back" is a PROMISE about behaviour that only exists when the report-jobs
 * subsystem is on. With `VITE_REPORT_JOBS` off there is no job, no tray and no
 * hand-off — the run simply keeps going in the foreground. Rendering the
 * sentence unconditionally would tell the user their run will survive them
 * navigating away, on a build where it will not. So the caller states whether
 * hand-off is real (`handsOffAfterMs`), and with nothing passed the band says
 * only what it can see: that the report is running.
 *
 * ## Why the threshold is not the string "twenty seconds"
 *
 * `QUEUE_HANDOFF_MS` is env-overridable (`VITE_QUEUE_HANDOFF_MS`), so a literal
 * goes false the moment anyone tunes it — the same failure as a hand-typed
 * retention string. The caller passes the number and the sentence is built from
 * it.
 */
import { useEffect, useState } from 'react'
import ReportBand from '@components/reports/ReportBand'
import { fmtClock } from './queueFormat'

/** "20 seconds" / "45 seconds" / "2 minutes" — for prose, not a clock. */
function spellThreshold(ms) {
  const secs = Math.round(ms / 1000)
  if (secs % 60 === 0 && secs >= 60) {
    const mins = secs / 60
    return `${mins} ${mins === 1 ? 'minute' : 'minutes'}`
  }
  return `${secs} seconds`
}

const RunningReportBand = ({
  reportName,
  startedAt,
  handsOffAfterMs = null,
  onCancel,
  cancelLabel = 'Cancel',
  className = '',
  /*
   * Optional DETERMINATE progress, `{ loaded, total }`.
   *
   * Only one caller has it — Aged A/R pages its rows in, so it genuinely knows
   * how far along it is — and it is kept because "Loading 1,200 of 4,519 rows"
   * is real information that an indeterminate spinner throws away. Everything
   * else passes nothing and gets the spinner, which is the honest rendering of
   * "we do not know how far along this is".
   *
   * Explicitly NOT wired to the generic engine's `progress.percent`: that comes
   * from CB's own status string, is frequently 0 for an entire run, and a bar
   * frozen at 0% while a report works is a worse statement than no bar.
   */
  progress = null,
}) => {
  /*
   * Seeded from `startedAt` rather than 0 so a remount mid-run (HMR, a filter
   * edit that re-renders the subtree) does not restart the clock at zero and
   * under-report how long the user has actually been waiting.
   */
  const [elapsed, setElapsed] = useState(() => (startedAt ? Math.max(0, Date.now() - startedAt) : 0))

  useEffect(() => {
    if (startedAt == null) return undefined
    /*
     * Recompute from the START each tick instead of accumulating +1000. An
     * interval that is throttled in a background tab (browsers clamp these to
     * ~1/s and much worse when the tab is hidden) would otherwise drift
     * permanently behind the wall clock, and this band's only job is to say how
     * long it has really been.
     *
     * `startedAt` is closed over rather than held in a ref: it is in the dep
     * array, so a new start tears this interval down and builds one reading the
     * new value. The ref this used to keep was both unnecessary and a
     * write-during-render (react-hooks/refs).
     *
     * There is no eager `setElapsed` before the interval — that is a
     * set-state-in-effect, and the honest fix is upstream: both call sites pass
     * `key={startedAt}`, so a new run gives this a new component identity and
     * the initializer above re-seeds. Without the key a second run would show
     * the previous run's elapsed for up to one second.
     */
    const id = setInterval(() => {
      setElapsed(Math.max(0, Date.now() - startedAt))
    }, 1000)
    return () => clearInterval(id)
  }, [startedAt])

  const clock = fmtClock(elapsed)
  // `> 0` on the total, not just presence: a `{loaded: 0, total: 0}` would divide
  // by zero into a NaN width, and a total of 0 is not a thing to show a bar for.
  const hasProgress = Number(progress?.total) > 0

  return (
    <ReportBand
      variant="running"
      className={className}
      action={onCancel ? { label: cancelLabel, onClick: onCancel } : undefined}
      data-testid="running-report-band"
      /*
       * `status`, not `alert`: a report starting is expected and polite, and an
       * assertive region would interrupt a screen-reader user mid-sentence every
       * time they pressed Run. ReportBand names itself from this content.
       */
      role="status"
    >
      <span className="font-medium">
        {reportName ? `Running ${reportName}` : 'Running your report'}
      </span>
      <span className="mt-0.5 block text-[13px] font-normal opacity-90">
        {handsOffAfterMs
          ? `If it passes ${spellThreshold(handsOffAfterMs)} it carries on in the background and you get your table back. `
          : 'This can take a moment on a large date range. '}
        {/*
          * aria-hidden with a tabular figure: a 1Hz-updating live region inside
          * a `status` band would re-announce the whole sentence every second and
          * make the band unusable with a screen reader. The information a
          * non-visual user needs here is "it is running", which the sentence
          * already carries, and the outcome is announced when the run lands.
          */}
        <span aria-hidden="true" className="font-medium tabular-nums">{clock}</span>
      </span>
      {hasProgress && (
        <span className="mt-1.5 flex w-full items-center gap-2">
          <span
            /* Keeps the role/aria-value* set the shell's own bar carried, so the
               determinate case stays announceable rather than becoming decoration
               on the way into the band. */
            role="progressbar"
            aria-label={`${reportName || 'Report'} progress`}
            aria-valuemin={0}
            aria-valuemax={progress.total}
            aria-valuenow={progress.loaded}
            className="h-1.5 w-40 overflow-hidden rounded-full bg-white/70"
          >
            <span
              className="block h-full bg-current transition-all"
              style={{ width: `${Math.min(100, Math.max(0, (progress.loaded / progress.total) * 100))}%` }}
            />
          </span>
          {/* "Loading …", not a bare count: the number is climbing, and the verb
              is what says so to someone who looks once. Wording preserved from
              the spinner card this band replaces, so the shell's existing
              assertion still describes the shipped string. */}
          <span className="text-[13px] tabular-nums">
            {`Loading ${Number(progress.loaded).toLocaleString()} of ${Number(progress.total).toLocaleString()} rows`}
          </span>
        </span>
      )}
    </ReportBand>
  )
}

export default RunningReportBand
